import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent5 as accent, useSlide5, Seg } from "./slide5.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 5 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide5.data.ts.
   The "journey of a memory" becomes a vertical timeline (clearer on a phone
   than the desktop's horizontal row). */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide5Mobile: React.FC = () => {
  const {
    meta: slide5Meta, journey: slide5Journey,
    forgotten: slide5Forgotten, gatekeeper: slide5Gatekeeper, opportunity: slide5Opportunity,
  } = useSlide5();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#8069b0", textTransform: "uppercase", marginBottom: 8 }}>
        {slide5Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide5Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide5Meta.titlePre}<span style={{ color: accent }}>{slide5Meta.titleAccentWord}</span>{slide5Meta.titlePost1} {slide5Meta.titlePost2}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#8a7ab0", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide5Meta.subtitle}
      </p>

      {/* nothing is forgotten */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide5Meta.forgottenLabel}</div>
        <p style={txt}>{renderSegs(slide5Forgotten, "#d8c0ff")}</p>
      </div>

      {/* gatekeeper */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide5Meta.gatekeeperLabel}</div>
        <p style={txt}>{renderSegs(slide5Gatekeeper, "#d8c0ff")}</p>
      </div>

      {/* journey — vertical timeline */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide5Meta.journeyLabel}</div>
        <div style={{ position: "relative", paddingLeft: 4 }}>
          {/* vertical connector line */}
          <div style={{ position: "absolute", left: 11, top: 8, bottom: 8, width: 1, background: "rgba(155,114,221,0.25)" }} />
          {slide5Journey.map((j, i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: i === slide5Journey.length - 1 ? 0 : 14, position: "relative", zIndex: 1 }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: j.color, border: "1px solid rgba(155,114,221,0.6)", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: 1.5, color: accent, textTransform: "uppercase", marginBottom: 3 }}>{j.stage}</div>
                <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.5, color: "#9bbdd6", margin: 0 }}>{j.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* opportunity callout */}
      <div style={{ background: "rgba(155,114,221,0.06)", border: "1px solid rgba(155,114,221,0.20)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide5Meta.opportunityLabel}</div>
        <p style={{ ...txt, color: "#d0c0f8" }}>{renderSegs(slide5Opportunity, "#ffffff")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide5Mobile;
